import { useCallback, useEffect, useMemo, useState } from 'react';
import { ITEMS_PER_PAGE } from '../_const';
import { TableDataType } from '../_types';

/**
 * 테이블 데이터 페이지네이션을 관리하는 커스텀 훅
 *
 * 주요 기능:
 * 1. 현재 페이지 상태 관리 및 페이지 전환
 * 2. 페이지당 항목 수(ITEMS_PER_PAGE) 기반 데이터 분할
 * 3. 현재 페이지에 표시할 데이터 계산 및 관리
 * 4. 총 페이지 수 계산 및 관리
 * 5. 현재 페이지의 시작/끝 항목 번호 계산
 * 6. 데이터 변경 시 첫 페이지로 자동 이동
 * 7. 페이지 이동을 위한 jump, prev, next 핸들러 제공
 *
 * @param {TableDataType[]} data - 페이지네이션할 전체 테이블 데이터
 * @param {Object} options - 페이지네이션 옵션
 * @param {Function} options.onPageChange - 페이지 변경 시 호출될 콜백 함수
 * @returns {Object} 페이지네이션 관련 상태와 함수
 * @returns {number} currentPage - 현재 페이지 번호
 * @returns {Function} jump - 특정 페이지로 이동하는 함수
 * @returns {Function} prev - 이전 페이지로 이동하는 함수
 * @returns {Function} next - 다음 페이지로 이동하는 함수
 * @returns {number} totalPages - 총 페이지 수
 * @returns {TableDataType[]} currentPageData - 현재 페이지에 표시할 데이터
 * @returns {number} currentPageEndNumber - 현재 페이지의 마지막 항목 번호
 * @returns {number} currentPageStartNumber - 현재 페이지의 첫 항목 번호
 */
export function usePagination(
  data: TableDataType[],
  options?: {
    onPageChange?: (page: number) => void;
  },
) {
  const [currentPage, setCurrentPage] = useState(1);

  // 총 페이지 수를 state가 아닌 useMemo로 계산
  const totalPages = useMemo(() => {
    return Math.ceil(data.length / ITEMS_PER_PAGE) || 1;
  }, [data]);

  // 페이지 변경 시 데이터 업데이트
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return data.slice(startIndex, endIndex);
  }, [currentPage, data]);

  // 데이터가 변경되면 첫 페이지로 이동
  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  // 페이지 변경 핸들러 (내부 상태 업데이트 + 외부 콜백 호출)
  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      options?.onPageChange?.(page);
    },
    [options?.onPageChange],
  );

  // 페이지 이동 핸들러 함수들
  const jump = useCallback(
    (page: number) => {
      // 인덱스 오버플로우 방지
      const targetPage = Math.max(1, Math.min(page, totalPages));
      handlePageChange(targetPage);
    },
    [totalPages, handlePageChange],
  );

  const prev = useCallback(() => {
    const prevPage = Math.max(1, currentPage - 1);
    handlePageChange(prevPage);
  }, [currentPage, handlePageChange]);

  const next = useCallback(() => {
    const nextPage = Math.min(totalPages, currentPage + 1);
    handlePageChange(nextPage);
  }, [currentPage, totalPages, handlePageChange]);

  // 현재 페이지의 시작/끝 항목 번호 계산
  const currentPageEndNumber = useMemo(() => {
    return Math.min(currentPage * ITEMS_PER_PAGE, data.length);
  }, [currentPage, data.length]);

  const currentPageStartNumber = useMemo(() => {
    return data.length > 0
      ? Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, data.length)
      : 0;
  }, [currentPage, data.length]);

  return {
    currentPage,
    jump,
    prev,
    next,
    totalPages,
    currentPageData,
    currentPageEndNumber,
    currentPageStartNumber,
  };
}
